export type Locale = 'en' | 'ro';

const en = {
  schedule: 'Schedule',
  loading: 'Loading schedule…',
  swapShifts: 'Swap shifts',
  cancelSwap: 'Cancel swap',
  exportPng: 'Export PNG',
  exporting: 'Exporting…',
  person: 'Person',
  total: 'total',
  previous: 'Previous',
  thisWeek: 'This week',
  next: 'Next',
  hoursLeft: '{hours} left',
  hoursOver: '{hours} over',
  swapHintFirst: 'Select the first shift, then the one to swap it with.',
  swapHintSecond:
    '{person} on {date} selected. Now pick the other shift.',
  off: 'Off',
  holiday: 'Holiday',
  empty: 'Empty',
  setHours: 'Set hours',
  pickStart: 'Pick a start time',
  pickEnd: 'Pick an end time',
  fromTime: 'From {time}',
  back: 'Back',
  delete: 'Delete',
  deleteAria: 'Delete {label}',
  cancel: 'Cancel',
  shiftChanges: 'Shift changes',
  noSwaps: 'No swaps yet. Use Swap shifts, then pick two cells.',
  when: 'When',
  from: 'From',
  before: 'Before',
  after: 'After',
  to: 'To',
  undo: 'Undo',
  exportTitleSameMonth:
    'Schedule for week {start}-{end} {monthYear}',
  exportTitleRange: 'Schedule for week {start}-{end}',
  morning: 'Morning',
  intermediary: 'Intermediary',
  afternoon: 'Afternoon',
  language: 'Language',
  loginTitle: 'Sign in',
  loginSubtitle: 'Sign in to open the schedule.',
  username: 'Username',
  password: 'Password',
  signIn: 'Sign in',
  invalidCredentials: 'Invalid username or password',
  authError: 'Something went wrong. Try again.',
  notFoundTitle: '404',
  notFoundMessage: 'Page not found',
  logout: 'Log out',
  checkingSession: 'Checking session…',
  profile: 'Profile',
  backToSchedule: 'Back to schedule',
  signedInAs: 'Signed in as {username}',
  changePassword: 'Change password',
  currentPassword: 'Current password',
  newPassword: 'New password',
  confirmPassword: 'Confirm new password',
  savePassword: 'Save password',
  passwordChanged: 'Password updated.',
  currentPasswordIncorrect: 'Current password is incorrect',
  passwordTooShort: 'New password must be at least 8 characters',
  passwordMismatch: 'New passwords do not match',
  passwordUnchanged: 'New password must be different from the current one',
};

export type MessageKey = keyof typeof en;

const ro: Record<MessageKey, string> = {
  schedule: 'Program',
  loading: 'Se încarcă programul…',
  swapShifts: 'Schimbă turele',
  cancelSwap: 'Anulează schimbul',
  exportPng: 'Exportă PNG',
  exporting: 'Se exportă…',
  person: 'Persoană',
  total: 'total',
  previous: 'Anterior',
  thisWeek: 'Săptămâna aceasta',
  next: 'Următor',
  hoursLeft: '{hours} rămase',
  hoursOver: '{hours} peste',
  swapHintFirst: 'Selectează prima tură, apoi pe cea cu care o schimbi.',
  swapHintSecond:
    '{person} pe {date} selectat. Alege acum cealaltă tură.',
  off: 'Liber',
  holiday: 'Concediu',
  empty: 'Gol',
  setHours: 'Setează orele',
  pickStart: 'Alege ora de început',
  pickEnd: 'Alege ora de sfârșit',
  fromTime: 'De la {time}',
  back: 'Înapoi',
  delete: 'Șterge',
  deleteAria: 'Șterge {label}',
  cancel: 'Anulează',
  shiftChanges: 'Schimbări de ture',
  noSwaps:
    'Nicio schimbare încă. Folosește Schimbă turele, apoi alege două celule.',
  when: 'Când',
  from: 'De la',
  before: 'Înainte',
  after: 'După',
  to: 'Către',
  undo: 'Anulează',
  exportTitleSameMonth:
    'Program pentru săptămâna {start}-{end} {monthYear}',
  exportTitleRange: 'Program pentru săptămâna {start}-{end}',
  morning: 'Dimineață',
  intermediary: 'Intermediar',
  afternoon: 'După-amiază',
  language: 'Limbă',
  loginTitle: 'Autentificare',
  loginSubtitle: 'Autentifică-te pentru a deschide programul.',
  username: 'Nume de utilizator',
  password: 'Parolă',
  signIn: 'Intră în cont',
  invalidCredentials: 'Nume de utilizator sau parolă invalidă',
  authError: 'A apărut o eroare. Încearcă din nou.',
  notFoundTitle: '404',
  notFoundMessage: 'Pagina nu a fost găsită',
  logout: 'Ieși din cont',
  checkingSession: 'Se verifică sesiunea…',
  profile: 'Profil',
  backToSchedule: 'Înapoi la program',
  signedInAs: 'Autentificat ca {username}',
  changePassword: 'Schimbă parola',
  currentPassword: 'Parola actuală',
  newPassword: 'Parola nouă',
  confirmPassword: 'Confirmă parola nouă',
  savePassword: 'Salvează parola',
  passwordChanged: 'Parola a fost actualizată.',
  currentPasswordIncorrect: 'Parola actuală este greșită',
  passwordTooShort: 'Parola nouă trebuie să aibă cel puțin 8 caractere',
  passwordMismatch: 'Parolele noi nu coincid',
  passwordUnchanged: 'Parola nouă trebuie să fie diferită de cea actuală',
};

const dictionaries: Record<Locale, Record<MessageKey, string>> = { en, ro };

const interpolate = (
  template: string,
  vars: Record<string, string | number>,
) =>
  template.replace(/\{(\w+)\}/g, (_, key: string) =>
    String(vars[key] ?? `{${key}}`),
  );

export const translate = (
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
) => {
  const message = dictionaries[locale][key];
  return vars ? interpolate(message, vars) : message;
};
